import React from 'react';

import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import { LogOutIcon } from 'lucide-react';

import {
  AppThemeEnum,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  buttonVariants,
  cn,
  CopyToClipboard,
  Label,
  NavigationMenuContent,
  NavigationMenuItem,
  Switch,
  useTheme,
} from '@sandtech-hub-monorepo/ui-kit';

import {
  useCognito,
  useGetMaybeViewer,
} from '@sandtech-hub-monorepo/ui-auth-kit';
import { getIcon, SETTINGS_MENU_ITEM_VALUE } from '../utils';
import { SettingsAppInterface } from '@sandtech-hub-monorepo/ui-kit-site';
import { HeaderLink } from './HeaderLink';

export interface SettingsProps {
  className?: string;
  apps: SettingsAppInterface[];
  sdk: boolean;
  hasThemeSwitcher: boolean;
  showUserHeader?: boolean;
}

export const Settings = ({
  className,
  apps,
  sdk,
  hasThemeSwitcher,
  showUserHeader = false,
}: SettingsProps) => {
  const { viewer } = useGetMaybeViewer();
  const { theme, toggleTheme } = useTheme();
  const { signOut } = useCognito();

  return (
    <NavigationMenuItem className={className} value={SETTINGS_MENU_ITEM_VALUE}>
      <NavigationMenuPrimitive.Trigger
        onPointerMove={(event) => event.preventDefault()}
        onPointerLeave={(event) => event.preventDefault()}
      >
        <Avatar>
          <AvatarFallback className="aspect-square h-full w-full">
            {`${viewer?.firstName?.charAt(0)}${
              viewer?.lastName?.charAt(0) || ''
            }`}
          </AvatarFallback>
          <AvatarImage
            src={viewer?.profilePhoto || ''}
            className="object-cover"
          />
        </Avatar>
      </NavigationMenuPrimitive.Trigger>

      <NavigationMenuContent
        className="w-[calc(100vw-3rem)] xs:w-[240px] md:w-[240px] pb-2"
        onPointerMove={(event) => event.preventDefault()}
        onPointerLeave={(event) => event.preventDefault()}
      >
        {showUserHeader && (
          <div className="flex flex-col gap-1 mx-4 mt-4 pb-3 border-b">
            <p className="font-semibold text-foreground truncate">
              {`${viewer?.firstName || ''} ${viewer?.lastName || ''}`.trim()}
            </p>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <p className="truncate">User ID: {viewer?.sandId}</p>
              {viewer?.sandId && <CopyToClipboard text={viewer.sandId} />}
            </div>
          </div>
        )}

        <ul className="flex flex-col my-4 gap-2 w-full">
          {hasThemeSwitcher && (
            <li className="flex gap-2 mx-4" key="theme-switch">
              <Switch
                id="theme-switch"
                checked={theme === AppThemeEnum.dark}
                onCheckedChange={toggleTheme}
              />
              <Label
                htmlFor="theme-switch"
                className="flex-1 my-auto text-foreground cursor-pointer"
              >
                {theme === AppThemeEnum.dark
                  ? 'View light theme'
                  : 'View dark theme'}
              </Label>
            </li>
          )}

          {apps.map((app) => {
            return (
              <li className="mx-4" key={app.name}>
                <HeaderLink
                  sdk={sdk}
                  key={`${app.name}-link`}
                  href={app.route}
                  className={cn(
                    buttonVariants({ variant: 'link', size: 'default' }),
                    'pl-0 hover:no-underline text-foreground'
                  )}
                  {...(app.openInNewTab
                    ? { target: '_blank', rel: 'noopener noreferrer' }
                    : {})}
                >
                  {getIcon(app.iconName, 'h-4')}
                  <p className="ml-2">{app.name}</p>
                </HeaderLink>

                {app.followedBySeparator && (
                  <div key={`${app.name}-separator`} className="border-b" />
                )}
              </li>
            );
          })}

          <li className="mx-4" key="logout">
            <Button
              variant="link"
              className="pl-0 hover:no-underline text-foreground"
              onClick={signOut}
            >
              <LogOutIcon className="h-4 w-auto" />
              <p className="ml-2">Log out</p>
            </Button>
          </li>
        </ul>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};
